/**
 * The "Import…" dropdown, shared by every surface that offers connection
 * import (Manage Connections, the start-page discovery card, the New
 * Connection dialog). Lists every import source in registry order; sources
 * with a store detected on this machine float to the top with a "Found" dot
 * and — when the caller passes `counts` — how many connections are waiting.
 * Picking a source scans + opens the review dialog (file-only sources jump
 * straight to Browse…).
 *
 * The New Connection dialog (Option C, C1) supplies its own trigger (a start-view
 * option or a rail row), may open the menu itself (Enter in the start filter),
 * and reuses `ImportSourceItems` inside its compact bar's "Add from…" menu.
 */
import { Fragment, useEffect, useMemo, useState, type ReactElement } from 'react';
import { PackageOpen } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { ReasonButton } from '../../../components/ui/ReasonButton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import type { ImportSourceId, ImportSourceInfo } from '../../../types/connectionImport';
import { listImportSources, openImportSource } from './connectionImportService';
import { importSourceGroups } from './importSourceGroups';
import type { ImportEntry } from './connectionImportStore';

interface ImportSourcesMenuProps {
  entry: ImportEntry;
  /** Called before opening a dialog (e.g. to close the parent Manage dialog). */
  onBeforeOpen?: () => void;
  /** Trigger label (default 'Import…'). */
  label?: string;
  /** Trigger button variant (default 'ghost' — an unobtrusive secondary action). */
  variant?: 'ghost' | 'outline' | 'default';
  /** Trigger `data-testid`; distinct per surface so several can coexist in the DOM. */
  testId?: string;
  className?: string;
  /** Importable-connection counts per source, shown beside the "Found" badge. */
  counts?: Partial<Record<ImportSourceId, number>>;
  /** Unavailable for now, with this reason (the New Connection dialog while a Save is in flight: "Saving…"). */
  disabledReason?: string;
  /** A custom trigger element (rendered `asChild`); the default is the "Import…" button. */
  trigger?: ReactElement;
  /** Controlled open state (the dialog opens the menu from its filter's Enter). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Where in the New Connection dialog the menu was opened (analytics only). */
  placement?: 'start' | 'rail' | 'compact';
  /** Radix's focus return on close; the dialog puts focus back into its filter instead of the trigger. */
  onCloseAutoFocus?: (event: Event) => void;
}

/** Refreshes the "Found" detection each time `open` turns true (cheap existsSync probes) and records the open. */
export function useImportSourceDetection(open: boolean, entry: ImportEntry, placement?: ImportSourcesMenuProps['placement']) {
  const [detected, setDetected] = useState<Set<ImportSourceId>>(new Set());
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void (async () => {
      const list: ImportSourceInfo[] = await listImportSources();
      if (cancelled) return;
      setDetected(new Set(list.filter((s) => s.detected).map((s) => s.id)));
    })();
    window.electronAPI?.analytics?.track('feature:import_menu_open', { entry, ...(placement ? { placement } : {}) })?.catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open, entry, placement]);
  return detected;
}

/**
 * The menu's items: sources found on this machine first, then Apps, Files and Azure (final UX review UX-17), each under
 * its label with a separator between them (registry order within each).
 */
export function ImportSourceItems({
  detected,
  counts,
  onPick,
}: {
  detected: Set<ImportSourceId>;
  counts?: Partial<Record<ImportSourceId, number>>;
  onPick: (id: ImportSourceId) => void;
}) {
  const sections = useMemo(() => importSourceGroups(detected), [detected]);

  const renderItem = (id: ImportSourceId, itemLabel: string, isDetected: boolean) => {
    const count = counts?.[id];
    return (
      <DropdownMenuItem
        key={id}
        onSelect={() => onPick(id)}
        data-testid={`import-source-item-${id}`}
        className="flex items-center gap-2"
      >
        <span
          className={`h-1.5 w-1.5 shrink-0 rounded-full ${
            isDetected ? 'bg-emerald-500' : 'bg-transparent'
          }`}
          aria-hidden="true"
        />
        <span className="flex-1">{itemLabel}</span>
        {isDetected && (
          <span className="text-[10px] text-emerald-600 dark:text-emerald-400 tabular-nums">
            {count && count > 0 ? `${count} found` : 'Found'}
          </span>
        )}
      </DropdownMenuItem>
    );
  };

  return (
    <>
      {sections.map((section, index) => (
        <Fragment key={section.id}>
          {index > 0 && <DropdownMenuSeparator />}
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-muted-foreground" data-testid={`import-source-group-${section.id}`}>
            {section.label}
          </DropdownMenuLabel>
          {section.sources.map((source) => renderItem(source.id, source.label, section.id === 'found'))}
        </Fragment>
      ))}
    </>
  );
}

export function ImportSourcesMenu({
  entry,
  onBeforeOpen,
  label = 'Import…',
  variant = 'ghost',
  testId = 'import-connections-button',
  className,
  counts,
  disabledReason,
  trigger,
  open: controlledOpen,
  onOpenChange,
  placement,
  onCloseAutoFocus,
}: ImportSourcesMenuProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = (next: boolean) => {
    if (controlledOpen === undefined) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };
  const detected = useImportSourceDetection(open, entry, placement);

  const pick = (id: ImportSourceId) => {
    setOpen(false);
    onBeforeOpen?.();
    openImportSource(id, entry);
  };

  if (disabledReason && !trigger) {
    return (
      <ReasonButton type="button" variant={variant} size="sm" className={`flex items-center gap-2 ${className ?? 'text-muted-foreground'}`} reason={disabledReason} data-testid={testId}>
        <PackageOpen className="h-4 w-4" />
        {label}
      </ReasonButton>
    );
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        {trigger ?? (
          <Button
            type="button"
            variant={variant}
            size="sm"
            className={`flex items-center gap-2 ${className ?? 'text-muted-foreground'}`}
            data-testid={testId}
          >
            <PackageOpen className="h-4 w-4" />
            {label}
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        // Next to its row (UX-17): beside the rail's row, above the start view's (Radix flipped it far from the row).
        side={placement === 'rail' ? 'right' : placement === 'start' ? 'top' : undefined}
        align={placement === 'rail' ? 'end' : 'start'}
        className="max-h-[60vh] overflow-y-auto"
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <ImportSourceItems detected={detected} counts={counts} onPick={pick} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
